import React, { useState } from 'react';
import { JobPosting, JobDepartment, JobType, ExperienceLevel } from '../types';
import { api } from '../services/api';
import { X, PlusCircle, ShieldAlert } from 'lucide-react';

interface CreateJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateJobModal: React.FC<CreateJobModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState<JobDepartment>('Development');
  const [type, setType] = useState<JobType>('Full-time');
  const [location, setLocation] = useState('Remote');
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel>('Intermediate');
  const [salaryRange, setSalaryRange] = useState('$50,000 - $70,000 / yr');
  const [description, setDescription] = useState('');
  const [requirements, setRequirements] = useState('');
  const [responsibilities, setResponsibilities] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.createJob({
        title,
        department,
        type,
        location,
        experience_level: experienceLevel,
        salary_range: salaryRange,
        description,
        requirements,
        responsibilities,
        is_featured: isFeatured ? 1 : 0,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create job posting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <X size={18} />
        </button>

        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800 }}>
            Create New Job Posting
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            Publish a new position to the public SansCraft Careers Portal (`careers.sanscraft.top`).
          </p>
        </div>

        {error && (
          <div
            style={{
              background: 'rgba(244, 63, 94, 0.1)',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              color: 'var(--accent-rose)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.85rem',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ShieldAlert size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Position Title</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Lead Network Systems Architect"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Department</label>
              <select
                className="form-select"
                value={department}
                onChange={(e) => setDepartment(e.target.value as JobDepartment)}
              >
                <option value="Development">Development</option>
                <option value="Infrastructure">Infrastructure</option>
                <option value="Moderation">Moderation</option>
                <option value="Game Design">Game Design</option>
                <option value="Marketing">Marketing</option>
                <option value="Systems">Systems</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Employment Type</label>
              <select
                className="form-select"
                value={type}
                onChange={(e) => setType(e.target.value as JobType)}
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Volunteer / Staff">Volunteer / Staff</option>
                <option value="Contract">Contract</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Experience Level</label>
              <select
                className="form-select"
                value={experienceLevel}
                onChange={(e) => setExperienceLevel(e.target.value as ExperienceLevel)}
              >
                <option value="Entry Level">Entry Level</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Senior">Senior</option>
                <option value="Lead">Lead</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Salary / Perks Range</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. $50,000 - $70,000 / yr or Staff Perks"
                value={salaryRange}
                onChange={(e) => setSalaryRange(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Role Description</label>
            <textarea
              className="form-textarea"
              placeholder="Provide a compelling overview of what the candidate will be doing..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Requirements & Qualifications</label>
            <textarea
              className="form-textarea"
              placeholder="List technical skills, years of experience, tools, and credentials required..."
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Key Responsibilities (Optional)</label>
            <textarea
              className="form-textarea"
              placeholder="Day-to-day duties and goals..."
              value={responsibilities}
              onChange={(e) => setResponsibilities(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0' }}>
            <input
              type="checkbox"
              id="isFeatured"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <label htmlFor="isFeatured" style={{ fontSize: '0.9rem', color: 'var(--text-main)', cursor: 'pointer' }}>
              Feature this job posting on the hero banner & top list
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-purple" disabled={loading}>
              <PlusCircle size={16} />
              {loading ? 'Publishing...' : 'Publish Job Posting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
